/**
 * ABD: the upgrades the product page's first screen accepts.
 *
 * Neither is required to buy an arrangement. `sections/abd-produto.liquid` posts a plain
 * `{% form 'product' %}` to /cart/add and every variant is an ordinary link, so a browser
 * that never runs this file still sells. What is here only makes the page nicer:
 *
 * 1. The gallery. Clicking a thumbnail swaps the photograph above it instead of loading
 *    a page. Without the script the `<noscript>` rules in the section turn the same
 *    markup into a plain column of every photograph.
 * 2. The quantity stepper. The number input is real and keeps working on its own; this
 *    adds the two buttons around it, which is what a phone wants and what the browser's
 *    own spinner is too small to be.
 * 3. "Saiba mais" on the add-on row. The boxes are real checkboxes named `items[][id]`,
 *    so a ticked one rides along with the plain POST (produto 01). What needs a script is
 *    the `<dialog>` behind each tile, so its button is rendered `hidden` and shown here.
 * 4. The add-on row's arrows, shown the same way, when there are more add-ons than fit.
 * 5. Comprar adds in place: the page stays and the header's count goes up.
 * 6. An add-on goes into the Carrinho the moment its tile is ticked (produto 04).
 * 7. Every add ends on the same receipt: a check, what went in, how many things the cart
 *    now holds, and a full-width "Ver carrinho" (produto 05).
 *
 * Horizon ships plain ES modules out of `assets/` and the theme has no build step, so
 * this is a module and nothing more.
 */

/** @param {HTMLElement} root */
const upgradeGallery = (root) => {
  const thumbs = /** @type {HTMLButtonElement[]} */ ([...root.querySelectorAll('.abd-produto__thumb')]);
  const piece = root.closest('.abd-produto__piece');
  const frame = piece?.querySelector('.abd-produto__frame');
  const image = frame?.querySelector('img');

  if (thumbs.length < 2 || !(image instanceof HTMLImageElement)) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const full = thumb.dataset.abdFull;
      const inner = thumb.querySelector('img');
      if (!full) return;

      image.src = full;
      // The swapped photograph is a different arrangement of the same product, so the
      // srcset that described the old one has to go with it rather than override src.
      image.srcset = inner?.srcset ?? '';
      image.sizes = '(width < 60rem) 100vw, 52vw';
      // And so does the description. Leaving the old alt in place means the picture on
      // screen and the sentence read out are two different photographs, which is worse
      // than either alone: it is wrong rather than missing.
      image.alt = thumb.dataset.abdAlt ?? image.alt;
      // The frame is sized from the photograph's ratio rather than from the file, which
      // is what keeps the page still while the first one loads (abd-produto.css). A swap
      // is a different photograph, so it is a different ratio, and the frame has to be
      // told before the new bytes arrive or the two disagree for as long as the download
      // takes: the box holds the old shape and the picture stretches inside it.
      if (thumb.dataset.abdRatio && frame instanceof HTMLElement) {
        frame.style.setProperty('--abd-piece-ratio', thumb.dataset.abdRatio);
      }

      thumbs.forEach((other) => {
        const current = other === thumb;
        other.classList.toggle('is-current', current);
        // The non-visual half of `is-current`. The section renders it on the leading
        // thumbnail and this is what moves it, so the two agree after a swap.
        if (current) other.setAttribute('aria-current', 'true');
        else other.removeAttribute('aria-current');
      });
    });
  });
};

/** @param {HTMLElement} root */
const upgradeQuantity = (root) => {
  const input = root.querySelector('.abd-qty__input');
  if (!(input instanceof HTMLInputElement)) return;

  /**
   * @param {'down' | 'up'} direction
   * @returns {HTMLButtonElement}
   */
  const makeButton = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `abd-qty__step abd-qty__step--${direction}`;
    // A drawn mark rather than a hyphen or a plus sign: the two glyphs do not match each
    // other at any size, and the second one is not a minus.
    button.innerHTML =
      direction === 'down'
        ? '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8h10" /></svg>'
        : '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 3v10M3 8h10" /></svg>';
    button.setAttribute('aria-label', direction === 'down' ? 'Diminuir a quantidade' : 'Aumentar a quantidade');
    button.addEventListener('click', () => {
      if (direction === 'down') input.stepDown();
      else input.stepUp();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    return button;
  };

  const shell = document.createElement('div');
  shell.className = 'abd-qty__control';
  input.replaceWith(shell);
  const down = makeButton('down');
  const up = makeButton('up');
  shell.append(down, input, up);
  root.classList.add('abd-qty--upgraded');

  /**
   * Greys the button that has nowhere left to go.
   *
   * Without this both buttons look and behave identically at every quantity, and pressing
   * minus on the one arrangement a customer is buying does nothing at all — no movement,
   * no sound, no message. A control that answers a press with silence reads as broken, and
   * `PRODUCT.md` describes an audience that abandons rather than investigates.
   *
   * `max` is often absent, because `quantity_rule.max` is only set on products Marina has
   * limited. No maximum means plus is never the end of anything, so it stays live.
   */
  const markBounds = () => {
    const value = Number(input.value);
    const min = Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const atMin = Number.isFinite(value) && Number.isFinite(min) && value <= min;
    const atMax = Number.isFinite(value) && value >= max;

    // Focus first, disabling second. A button that is disabled while it holds focus drops
    // it on `<body>`, and the customer's next Tab starts again from the top of the page —
    // WCAG 2.2 SC 2.4.3. The input is where the value is, so that is where focus belongs.
    if ((atMin && document.activeElement === down) || (atMax && document.activeElement === up)) {
      input.focus();
    }

    down.disabled = atMin;
    up.disabled = atMax;
  };

  input.addEventListener('input', markBounds);
  input.addEventListener('change', markBounds);
  markBounds();
};

/** @param {HTMLElement} root */
const upgradeAlgoMais = (root) => {
  root.querySelectorAll('[data-abd-algo-abre]').forEach((mais) => {
    if (!(mais instanceof HTMLButtonElement)) return;
    const dialog = document.getElementById(mais.dataset.abdAlgoAbre ?? '');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const box = document.getElementById(mais.dataset.abdAlgoAbre?.replace(/-dialog$/, '') ?? '');
    if (!(box instanceof HTMLInputElement)) return;

    mais.hidden = false;
    mais.addEventListener('click', () => {
      // The photograph ships lazy so four closed dialogs cost nothing on arrival. A lazy image
      // inside a dialog that was closed when the page laid out is not fetched in time for the
      // opening, and the panel showed an empty box where the picture should be.
      dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        if (img instanceof HTMLImageElement) img.loading = 'eager';
      });
      dialog.showModal();
    });

    // However the dialog closes — Adicionar, Fechar or Esc — focus lands on the tile's
    // checkbox rather than on "Saiba mais", where the browser would put it: the box is what
    // the customer came back to decide about, and after Adicionar it is what just changed.
    dialog.addEventListener('close', () => box.focus());

    dialog.querySelectorAll('[data-abd-algo-fecha]').forEach((fecha) => {
      fecha.addEventListener('click', () => dialog.close());
    });

    dialog.querySelectorAll('[data-abd-algo-adiciona]').forEach((adiciona) => {
      adiciona.addEventListener('click', () => {
        // Ticks, never toggles: "Adicionar" on something already chosen must not unchoose it,
        // and must not announce a change either, since a tick is a purchase (produto 04).
        if (!box.checked) {
          box.checked = true;
          box.dispatchEvent(new Event('change', { bubbles: true }));
        }
        dialog.close();
      });
    });
  });
};

/**
 * The arrows either side of the add-on row (produto 02).
 *
 * The row sizes its tiles so a whole number of them fill it and snaps on whole tiles
 * (abd-produto.css), so an arrow only has to scroll by the row's own width and the snap
 * lands it. The arrows are shown only while the row holds more than it shows; four add-ons
 * in a wide column fit, and two arrows that can do nothing are two more things to read.
 *
 * @param {HTMLElement} root
 */
const upgradeCarrossel = (root) => {
  const carrossel = root.querySelector('.abd-algo__carrossel');
  const row = root.querySelector('.abd-algo__row');
  if (!(carrossel instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

  const setas = /** @type {HTMLButtonElement[]} */ ([...carrossel.querySelectorAll('[data-abd-algo-seta]')]);
  if (setas.length !== 2) return;
  const [prev, next] = setas;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const markEnds = () => {
    const atStart = row.scrollLeft <= 1;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    // Focus first, disabling second, as the quantity stepper does: a disabled button drops
    // focus on `<body>`. The other arrow is the one that can still move, and it has to be
    // enabled before it can take the focus, since it was the disabled one a moment ago.
    if (!atStart) prev.disabled = false;
    if (!atEnd) next.disabled = false;
    if (atStart && document.activeElement === prev) next.focus();
    if (atEnd && document.activeElement === next) prev.focus();
    prev.disabled = atStart;
    next.disabled = atEnd;
  };

  const fit = () => {
    // Measured with the arrows out, which is the widest the row can be. The tiles are sized
    // from the count that fits, not from the arrows, so what overflows here overflows with
    // the arrows in too.
    carrossel.classList.remove('abd-algo--setas');
    const overflows = row.scrollWidth > row.clientWidth + 1;
    carrossel.classList.toggle('abd-algo--setas', overflows);
    setas.forEach((seta) => {
      seta.hidden = !overflows;
    });
    if (overflows) markEnds();
  };

  setas.forEach((seta) => {
    seta.addEventListener('click', () => {
      const direction = Number(seta.dataset.abdAlgoSeta) || 1;
      row.scrollBy({ left: direction * row.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
    });
  });

  row.addEventListener('scroll', markEnds, { passive: true });
  new ResizeObserver(fit).observe(carrossel);
  fit();
};

/** The theme's cart routes, or Shopify's own when a test page has no `Theme`. */
const cartRoutes = () => {
  const routes = typeof Theme === 'undefined' ? null : Theme.routes;
  return {
    addUrl: routes?.cart_add_url ?? '/cart/add.js',
    changeUrl: routes?.cart_change_url ?? '/cart/change.js',
    cartUrl: routes?.cart_url ?? '/cart',
  };
};

/** Every status line on the page: the one under Comprar and the one under the add-ons. */
const avisos = () => document.querySelectorAll('[data-abd-aviso], [data-abd-algo-aviso]');

/**
 * Writes a plain sentence into a status line: an error, or nothing at all.
 *
 * @param {HTMLElement} aviso
 * @param {string} text
 * @param {{ error?: boolean }} [options]
 */
const say = (aviso, text, { error = false } = {}) => {
  aviso.replaceChildren(document.createTextNode(text));
  aviso.classList.toggle('is-error', error);
  aviso.classList.remove('is-feito');
};

/** @param {number} count */
const itens = (count) => (count === 1 ? '1 item no carrinho' : `${count} itens no carrinho`);

/**
 * The receipt after an add or a removal (produto 05).
 *
 * "Ver carrinho" used to be an underlined word at the end of a 15px sentence, the same
 * weight as "Saiba mais", and read as a footnote rather than as the next step. Now it is a
 * button as wide and as tall as Comprar, directly under it. It spends no colour to be
 * found: an ink outline on cream, because the page's one rose is Comprar (the Rare Verb
 * Rule), and the check beside "Adicionado" is green, the shop's mark for a thing it has
 * registered. The count answers the question a quiet press leaves: did it go in twice?
 *
 * Only one receipt is on the page at a time. A tick under the 13h line clears the one
 * under Comprar, so there are never two "Ver carrinho" buttons saying different counts.
 *
 * @param {HTMLElement} aviso
 * @param {{ title: string, detail: string, count: number, cartUrl: string }} receipt
 */
const confirmar = (aviso, { title, detail, count, cartUrl }) => {
  avisos().forEach((other) => {
    if (other !== aviso && other instanceof HTMLElement) say(other, '');
  });

  const linha = document.createElement('p');
  linha.className = 'abd-feito__linha';
  linha.innerHTML =
    '<svg class="abd-feito__marca" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 8.4l2.6 2.6L12 5.4" /></svg>';
  const texto = document.createElement('span');
  texto.className = 'abd-feito__texto';
  const titulo = document.createElement('strong');
  titulo.className = 'abd-feito__titulo';
  titulo.textContent = title;
  const conta = document.createElement('span');
  conta.className = 'abd-feito__conta abd-num';
  const quantos = document.createElement('span');
  quantos.className = 'abd-feito__quantos';
  quantos.textContent = itens(count);
  if (detail) conta.append(document.createTextNode(`${detail} · `));
  conta.append(quantos);
  texto.append(titulo, conta);
  linha.append(texto);

  const ir = document.createElement('a');
  ir.className = 'abd-feito__ir';
  ir.href = cartUrl;
  ir.textContent = 'Ver carrinho';

  aviso.classList.remove('is-error');
  // Removed and added back, so the arrival plays on every receipt rather than only the first.
  aviso.classList.remove('is-feito');
  void aviso.offsetWidth;
  aviso.classList.add('is-feito');
  aviso.replaceChildren(linha, ir);
};

/**
 * Posts JSON to a cart endpoint and returns the body, or throws Shopify's own message.
 *
 * @param {string} url
 * @param {object} payload
 */
const postCart = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.description || body.message || 'Não foi possível adicionar. Tente de novo.');
  }
  return body;
};

/**
 * Tells the header, and anything else listening, what the cart holds now.
 *
 * The header's count is Horizon's `cart-icon`, and it listens for Shopify's standard cart
 * event, so the count moves the way it would for Horizon's own form.
 *
 * @param {EventTarget} target
 * @param {{ item_count: number, items: unknown[] }} cart
 * @param {'add' | 'update'} action
 * @param {{ merchandiseId: string, quantity: number }[] | { id: string, quantity: number }[]} lines
 *   `add` names the variant and how many were added; `update` names the cart line and the
 *   quantity it has now, which is what Shopify's event types ask of each.
 */
const announce = async (target, cart, action, lines) => {
  try {
    // Through the theme's import map, which a test page loading this module on its own does
    // not have; a string in a variable so `tsc` does not try to resolve it either.
    const specifier = '@shopify/events';
    const { CartLinesUpdateEvent } = await import(specifier);
    const deferred = CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'abd-produto', didError: false },
    });
  } catch {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      const render = /** @type {{ renderCartBubble?: (count: number) => void }} */ (icon).renderCartBubble;
      render?.call(icon, cart.item_count);
    });
  }
};

/** @param {string} cartUrl */
const readCart = async (cartUrl) => (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();

/**
 * Comprar adds to the Carrinho and the page stays (produto 02).
 *
 * Without this file the form posts to /cart/add and lands on the Carrinho. With it, the
 * same lines go to /cart/add.js in one request: the product at its quantity and each ticked
 * add-on at one, which is what the plain POST sends. The header's count is Horizon's
 * `cart-icon`, and it listens for Shopify's standard cart event, so the count moves the way
 * it would for Horizon's own form. The status line under the button says what happened and
 * links to the Carrinho, since a press that changes nothing on the screen but a small
 * number reads as a press that did nothing.
 *
 * Nothing here opens a drawer or reaches checkout. The Carrinho is still the only way
 * there (ADR 0001); the Comprador just decides when to go.
 *
 * @param {HTMLFormElement} form
 */
const upgradeCompra = (form) => {
  const button = form.querySelector('button[name="add"]');
  const aviso = form.querySelector('[data-abd-aviso]');
  if (!(button instanceof HTMLButtonElement) || !(aviso instanceof HTMLElement)) return;

  const label = button.textContent ?? '';
  const { addUrl, cartUrl } = cartRoutes();

  form.addEventListener('submit', async (event) => {
    if (button.disabled) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;

    // Only the product. The add-ons went in when they were ticked (produto 04), and the
    // boxes that still say `form="abd-produto-form"` are for the page without this script.
    const data = new FormData(form);
    const items = [{ id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) }];

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adicionando…';
    say(aviso, '');

    try {
      await postCart(addUrl, { items });
      const cart = await readCart(cartUrl);
      const product = form.closest('.abd-produto__buy')?.querySelector('.abd-produto__title')?.textContent?.trim() ?? '';
      await announce(
        form,
        cart,
        'add',
        items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      );
      confirmar(aviso, { title: 'Adicionado ao carrinho', detail: product, count: cart.item_count, cartUrl });
    } catch (error) {
      say(aviso, error instanceof TypeError || !(error instanceof Error)
        ? 'Não foi possível adicionar. Confira a conexão e tente de novo.'
        : error.message, { error: true });
    } finally {
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  });
};

/**
 * An add-on is bought by ticking it (produto 04).
 *
 * Ticking a tile adds one of it to the Carrinho at once; unticking takes one back out. Each
 * change waits for the one before it, so a quick tick and untick reach the cart in the order
 * they were made. When the cart refuses, the tick goes back to what the cart holds and the
 * status line under the row says why.
 *
 * The tiles start unticked on every visit, whatever the cart already holds: a tick means
 * "added from this page", which is what unticking can safely undo.
 *
 * @param {HTMLElement} root
 */
const upgradeAlgoCompra = (root) => {
  const aviso = root.querySelector('[data-abd-algo-aviso]');
  if (!(aviso instanceof HTMLElement)) return;
  const { addUrl, changeUrl, cartUrl } = cartRoutes();

  /** @type {Promise<void>} */
  let queue = Promise.resolve();

  root.querySelectorAll('.abd-algo__check').forEach((box) => {
    if (!(box instanceof HTMLInputElement)) return;
    const name = box.closest('.abd-algo__item')?.querySelector('.abd-algo__name')?.textContent?.trim() ?? '';

    box.addEventListener('change', () => {
      const adding = box.checked;
      const id = Number(box.value);

      queue = queue.then(async () => {
        try {
          let cart;
          if (adding) {
            await postCart(addUrl, { items: [{ id, quantity: 1 }] });
            cart = await readCart(cartUrl);
            await announce(root, cart, 'add', [{ merchandiseId: String(id), quantity: 1 }]);
          } else {
            cart = await readCart(cartUrl);
            const line = cart.items.find((/** @type {{ variant_id: number }} */ item) => item.variant_id === id);
            // Already gone, taken out on the Carrinho in another tab, say: nothing to undo.
            if (line) {
              const quantity = Math.max(0, line.quantity - 1);
              cart = await postCart(changeUrl, { id: line.key, quantity });
              await announce(root, cart, 'update', [{ id: line.key, quantity }]);
            }
          }
          confirmar(aviso, {
            title: adding ? 'Adicionado ao carrinho' : 'Retirado do carrinho',
            detail: name,
            count: cart.item_count,
            cartUrl,
          });
        } catch (error) {
          box.checked = !adding;
          say(aviso, error instanceof TypeError || !(error instanceof Error)
            ? 'Não foi possível mudar o carrinho. Confira a conexão e tente de novo.'
            : error.message, { error: true });
        }
      });
    });
  });
};

document.querySelectorAll('#abd-produto-form').forEach((node) => {
  if (node instanceof HTMLFormElement) upgradeCompra(node);
});

document.querySelectorAll('[data-abd-algo]').forEach((node) => {
  if (!(node instanceof HTMLElement)) return;
  upgradeAlgoMais(node);
  upgradeAlgoCompra(node);
  upgradeCarrossel(node);
});

document.querySelectorAll('[data-abd-gallery]').forEach((node) => {
  if (node instanceof HTMLElement) upgradeGallery(node);
});

document.querySelectorAll('.abd-qty').forEach((node) => {
  if (node instanceof HTMLElement) upgradeQuantity(node);
});
